import React, { ReactNode, useEffect, useMemo, useState } from "react"
import ReadLearnCategory from "./ReadLearnCategory"
import { useContentfulConditionalGate } from "#/lib/contentful/components/ContentfulConditionGate"
import { toCondition } from "#/lib/contentful/utils/conditionUtils"
import { useDoesUserSatisfyConditions } from "#/services/contentful/conditional-gate"
import { FormattedMessage, useIntl } from "react-intl"
// eslint-disable-next-line no-restricted-imports
import { SimpleGrid } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import { Redirect, useHistory } from "react-router"
import slugify from "slugify"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageHeader, PageHeaderGlyph } from "#/features/global-ui/page-header"
import { getOrderTagId, sortByJourneySpecificOrder } from "#/lib/contentful/utils/mappingUtils"
import { journeyOrderMapping, Journey } from "#/lib/contentful/utils/journeyOrderMapping"
import {
  Box,
  Button,
  DownIcon,
  Stack,
  Tile,
  Typography,
  UpIcon,
  BookmarkIcon,
  Grid,
  GoalsIllustration,
  useTheme
} from "@carrotfertility/carotene-core"
import { mapContentToCategory } from "./shared/CategoryMapper"
import { getHeap } from "#/pages/groups/utils/heap"
import { EntryWithoutUnresolvableLinks } from "#/utils/ContentTypes"
import { TypeCategorySkeleton } from "#/types/contentful"
import { useFlags } from "launchdarkly-react-client-sdk"
import { CarrotAcademySearch } from "./CarrotAcademySearch"
import ArticleThumbnail from "#/components/read/ArticleThumbnail"
import { useWindowEvent } from "#/utils/hooks/useWindowEvent"
import { ErrorPage } from "#/components/views/app/ErrorPage"
import { useMemberBookmarkedContent } from "#/components/read/hooks/useMemberBookmarkedContent"
import useGetMemberJourneyData from "#/hooks/useGetMemberJourneyData"

type ReadLearnCategoryGridProps = {
  categories: EntryWithoutUnresolvableLinks<TypeCategorySkeleton>[]
}
export const ReadLearnHomeMasterLayout = ({ children }: { children: ReactNode }): JSX.Element => {
  const { showCarrotAcademySearch } = useFlags()
  return (
    <PageLayout
      header={
        <PageHeader
          startGlyph={
            <PageHeaderGlyph src="/images/icn-lightbulb-color.png" srcSet={"/images/icn-lightbulb-color-2x.png"} />
          }
          pageTitle={<FormattedMessage defaultMessage="Carrot Academy" />}
        />
      }
    >
      {showCarrotAcademySearch ? (
        <Box
          sx={(theme) => ({
            marginBlockEnd: theme.spacing(theme.tokens.spacing.md)
          })}
        >
          <CarrotAcademySearch size="medium" />
        </Box>
      ) : null}
      {children}
    </PageLayout>
  )
}

export default function ReadLearnLandingPageLayout({ categories }: ReadLearnCategoryGridProps): JSX.Element {
  const theme = useTheme()

  const history = useHistory()
  const memberJourneyQuery = useGetMemberJourneyData()
  const { doesUserSatisfyConditions, isLoading: isConditionGateLoading } = useDoesUserSatisfyConditions(true)
  const journey = memberJourneyQuery.data?.currentMemberJourney.preferSubmittedRouting.journey
  const [bookmarksPanelOpen, setBookmarksPanelOpen] = useState(false)
  const useMemberBookmarkedContentQuery = useMemberBookmarkedContent(categories)
  const [windowWidth, setWindowWidth] = React.useState({
    width: window.innerWidth
  })
  const intl = useIntl()
  const bookmarks = useMemberBookmarkedContentQuery?.data

  const handleResize = () => {
    setWindowWidth({
      width: window.innerWidth
    })
  }

  useWindowEvent("resize", handleResize)

  useEffect(() => {
    if (journey) {
      getHeap().track("R&L Journey Banner Rendered", {
        "Member Journey": journey
      })
    }
  }, [journey])

  const mappedCategories = categories?.map(mapContentToCategory)

  const highlightedCategory = useMemo(() => {
    if (isConditionGateLoading) return null
    if (!journey || !journeyOrderMapping[journey].value) return null

    return (
      mappedCategories.find((category) => {
        if (journeyOrderMapping[journey].value !== getOrderTagId(category)) return false
        const conditions = category.conditions?.map(toCondition).filter(Boolean) ?? []
        return doesUserSatisfyConditions(conditions)
      }) ?? null
    )
  }, [mappedCategories, journey, isConditionGateLoading, doesUserSatisfyConditions])

  const gridCategories = useMemo(() => {
    return highlightedCategory
      ? mappedCategories.filter((category) => category.id !== highlightedCategory.id)
      : mappedCategories
  }, [highlightedCategory, mappedCategories])

  const sortedCategories = useMemo(() => {
    return gridCategories.sort((a, b) => {
      if (journey) {
        return sortByJourneySpecificOrder(journey as Journey, a, b)
      }
      return a.order - b.order
    })
  }, [gridCategories, journey])

  const categoriesAndConditions = useMemo(() => {
    return sortedCategories.map((category) => ({
      element: <React.Fragment key={category.id} />,
      conditions: category?.conditions,
      category
    }))
  }, [sortedCategories])

  const highlightedCategoryDescriptionAndConditions = useMemo(() => {
    return highlightedCategory?.description
      ? highlightedCategory.description.map((description) => ({
          element: <Typography>{description?.fields?.body}</Typography>,
          conditions: description?.fields?.conditions,
          description
        }))
      : []
  }, [highlightedCategory])

  const filteredElements = useContentfulConditionalGate(categoriesAndConditions, true)
  const filteredCategories = useMemo(() => {
    return categoriesAndConditions
      .filter((item) => filteredElements.includes(item.element))
      .map((item) => item.category)
  }, [categoriesAndConditions, filteredElements])
  const readAndLearnNonHighlightedCategories = filteredCategories.map((category, index) => (
    <ReadLearnCategory key={`id-${category?.navTitle ?? category?.title}`} category={category} index={index} />
  ))
  const readAndLearnHighlightedCategory = useContentfulConditionalGate(
    highlightedCategoryDescriptionAndConditions,
    true
  )

  if (
    !memberJourneyQuery.isLoading &&
    filteredCategories.length === 1 &&
    readAndLearnHighlightedCategory.length === 0
  ) {
    const matchingCategory = filteredCategories[0]
    return <Redirect to={`/read/${slugify(matchingCategory.navTitle)}`} />
  }

  if (useMemberBookmarkedContentQuery.isError) {
    return (
      <PageLayout>
        <ErrorPage />
      </PageLayout>
    )
  }

  return (
    <ReadLearnHomeMasterLayout>
      {!useMemberBookmarkedContentQuery?.isLoading && bookmarks?.length ? (
        <>
          <Box
            display="flex"
            alignItems="center"
            sx={{ marginBlockEnd: (theme) => theme.tokens.spacing.lg, cursor: "pointer" }}
            onClick={() => setBookmarksPanelOpen(!bookmarksPanelOpen)}
          >
            <BookmarkIcon color="secondary" />
            <Typography variant="body1">
              {intl.formatMessage({ defaultMessage: "Saved ({count})" }, { count: bookmarks.length })}
            </Typography>
            {bookmarksPanelOpen ? <UpIcon color="primary" /> : <DownIcon color="primary" />}
          </Box>
          {bookmarksPanelOpen && (
            <Box sx={{ marginBlockEnd: (theme) => theme.tokens.spacing.xxxl }}>
              <Grid container spacing={4}>
                {bookmarks.map((bookmark) => (
                  <Grid item xs={12} sm={12} md={12} lg={windowWidth.width < 1200 ? 6 : 4} key={bookmark?.articleId}>
                    <ArticleThumbnail
                      contentType={bookmark?.contentTypeId}
                      article={bookmark?.article}
                      category={bookmark?.category}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      ) : (
        <Box display="flex" alignItems="flex-start" sx={{ marginBlockEnd: (theme) => theme.tokens.spacing.lg }}>
          <BookmarkIcon color="disabled" />
          <Typography variant="body1">
            {intl.formatMessage({ defaultMessage: "Saved (0) · Your saved resources will show up here." })}
          </Typography>
        </Box>
      )}
      <Stack spacing={theme.tokens.spacing.lg}>
        {highlightedCategory && (
          <Tile
            variant="primary"
            color="blue"
            illustration={<GoalsIllustration color="blue" />}
            sx={{ position: "relative", overflow: "hidden" }}
            data-analytics-id="read-and-learn-journey-banner"
            data-member-journey={journey}
          >
            <Box sx={{ position: "relative", maxInlineSize: ["100%", "100%", "70%", "50%"] }}>
              <Stack
                gap={(theme) => theme.tokens.spacing.lg}
                marginTop={(theme) => theme.tokens.spacing.xl}
                marginBottom={(theme) => theme.tokens.spacing.xxxl}
              >
                <Typography variant="h3">{highlightedCategory?.title}</Typography>
                {readAndLearnHighlightedCategory}
              </Stack>
              <Button
                color="primary"
                size="small"
                onClick={() => {
                  history.push(`/read/${slugify(highlightedCategory?.navTitle)}`)
                }}
                data-analytics-id="read-and-learn-journey-banner-start-learning-button"
              >
                <FormattedMessage defaultMessage="Start learning" />
              </Button>
            </Box>
          </Tile>
        )}
        <SimpleGrid spacing={["medium", "medium", "32px"]} columns={[1, 2, 2, 3]}>
          {!memberJourneyQuery.isLoading && readAndLearnNonHighlightedCategories}
        </SimpleGrid>
      </Stack>
    </ReadLearnHomeMasterLayout>
  )
}
